import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { PillSearchBar } from "@/components/common/PillSearchBar";
import { FloatingControlsBar } from "@/components/projects/FloatingControlsBar";
import { useToast } from "@/components/Toast";
import {
  ClientHasProjectsError,
  useClientsData,
  type ClientInput,
  type ClientWithProjects,
} from "@/hooks/useClientsData";
import { fuzzyScoreFields } from "@/utils/fuzzySearch";
import { Plus } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { ClientModal } from "./ClientModal";
import { ClientsTable } from "./ClientsTable";

interface ClientsTabProps {
  // Rendered as the first child of the board (the page-level tab switcher).
  header?: ReactNode;
}

/** Score a client against a single search term across its searchable fields. */
function clientMatchScore(client: ClientWithProjects, term: string): number {
  return fuzzyScoreFields(
    [
      client.name,
      client.location,
      client.contact_name,
      client.contact_email,
      client.contact_phone,
    ],
    term,
  );
}

type ModalState = { mode: "add" } | { mode: "edit"; client: ClientWithProjects } | null;

/**
 * "Clients" tab - search, create, edit, and delete clients, each showing its
 * linked projects. Mirrors the Archived tab's floating search controls.
 */
export function ClientsTab({ header }: ClientsTabProps) {
  const { clients, loading, error, createClient, updateClient, deleteClient } = useClientsData();
  const { addToast } = useToast();

  const [searchPills, setSearchPills] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [modal, setModal] = useState<ModalState>(null);

  const activeSearchTerms = useMemo(
    () => [...searchPills, ...(searchInput.trim() ? [searchInput.trim()] : [])],
    [searchPills, searchInput],
  );

  const filteredClients = useMemo(() => {
    if (activeSearchTerms.length === 0) return clients;
    return clients.filter((client) =>
      activeSearchTerms.some((term) => clientMatchScore(client, term) > 0),
    );
  }, [clients, activeSearchTerms]);

  const handleSave = async (input: ClientInput) => {
    if (modal?.mode === "edit") {
      await updateClient(modal.client.id, input);
      addToast(`Updated ${input.name}`, "success");
    } else {
      await createClient(input);
      addToast(`Added ${input.name}`, "success");
    }
  };

  const handleDelete = async (client: ClientWithProjects) => {
    try {
      await deleteClient(client.id);
      addToast(`Deleted ${client.name}`, "success");
      setModal(null);
    } catch (err) {
      if (err instanceof ClientHasProjectsError) {
        addToast(
          `Can't delete ${client.name}: ${err.count} linked project${err.count === 1 ? "" : "s"}. Reassign them first.`,
          "warning",
        );
      } else {
        addToast(
          err instanceof Error ? err.message : `Failed to delete ${client.name}`,
          "error",
        );
      }
      // Rethrow so the modal can re-enable its Delete button.
      throw err;
    }
  };

  return (
    <BoardLayout className="clients-board">
      {header}
      <FloatingControlsBar>
        <div className="board-controls__search-row">
          <PillSearchBar
            pills={searchPills}
            onPillsChange={setSearchPills}
            inputValue={searchInput}
            onInputChange={setSearchInput}
            placeholder="Search clients by name, location, or contact..."
          />
          <button
            type="button"
            className="clients-tab__add-button"
            onClick={() => setModal({ mode: "add" })}
          >
            <Plus size={16} />
            Add Client
          </button>
        </div>
      </FloatingControlsBar>

      {error && <ErrorBanner message={error} entityName="clients" />}

      {loading ? (
        <LoadingState message="Loading clients..." />
      ) : (
        <div className="board-content">
          <ClientsTable
            clients={filteredClients}
            onEdit={(client) => setModal({ mode: "edit", client })}
          />
        </div>
      )}

      {modal && (
        <ClientModal
          client={modal.mode === "edit" ? modal.client : undefined}
          projectCount={modal.mode === "edit" ? modal.client.projects.length : 0}
          onClose={() => setModal(null)}
          onSave={handleSave}
          onDelete={
            modal.mode === "edit" ? () => handleDelete(modal.client) : undefined
          }
        />
      )}
    </BoardLayout>
  );
}
